import { Checkbox, cn } from '@hemmafixa/ui';
import { Check, ChevronDown } from 'lucide-react';
import { useState, type Dispatch, type SetStateAction } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { useCategories, useCounties } from '../shared/catalogue';

interface ServicesPickerProps {
  categories: string[];
  counties: string[];
  onCategories: Dispatch<SetStateAction<string[]>>;
  onCounties: Dispatch<SetStateAction<string[]>>;
  errors: { categories?: string; counties?: string };
}

/**
 * What the company does and where. Ticking a main category ticks all its services; open it to untick the ones
 * the company does not do. A main category counts as chosen only while all its services are.
 */
export function ServicesPicker({
  categories,
  counties,
  onCategories,
  onCounties,
  errors,
}: ServicesPickerProps) {
  const { t, language } = useI18n();
  const s = t.provider.onboarding;
  const tree = useCategories();
  const countyList = useCounties();
  const mains = tree.data?.items ?? [];
  const selected = new Set(categories);
  const name = (item: { nameSv: string; nameEn: string }) => (language === 'sv' ? item.nameSv : item.nameEn);
  // Categories with some services chosen start open, so the choice is visible
  const [open, setOpen] = useState<Set<string>>(
    () =>
      new Set(
        mains
          .filter((main) => main.children.some((child) => selected.has(child.slug)))
          .map((main) => main.slug),
      ),
  );

  const toggleMain = (slug: string, childSlugs: string[], on: boolean) =>
    onCategories((list) => {
      const next = new Set(list);
      for (const item of [slug, ...childSlugs]) {
        if (on) next.add(item);
        else next.delete(item);
      }
      return [...next];
    });

  const toggleChild = (main: string, siblings: string[], slug: string, on: boolean) =>
    onCategories((list) => {
      const next = new Set(list);
      // A main category saved on its own means all its services; spell them out before changing one
      if (next.has(main)) siblings.forEach((item) => next.add(item));
      if (on) next.add(slug);
      else next.delete(slug);
      // The main category follows its services: chosen when all are
      if (siblings.every((item) => next.has(item))) next.add(main);
      else next.delete(main);
      return [...next];
    });

  const allCounties = (countyList.data ?? []).map((county) => county.code);
  const everyCounty = allCounties.length > 0 && allCounties.every((code) => counties.includes(code));

  return (
    <div className="grid gap-6">
      <fieldset className="grid gap-3" aria-describedby={errors.categories ? 'categories-error' : undefined}>
        <legend className="mb-1 text-sm font-semibold text-secondary">{s.categories}</legend>
        {errors.categories && (
          <p id="categories-error" role="alert" className="text-sm text-danger">
            {errors.categories}
          </p>
        )}
        <div className="grid items-start gap-2.5 md:grid-cols-2">
          {mains.map((main) => {
            const childSlugs = main.children.map((child) => child.slug);
            // The main category on its own covers all its services
            const isOn = (slug: string) => selected.has(main.slug) || selected.has(slug);
            const chosen = childSlugs.filter(isOn).length;
            const state =
              childSlugs.length === 0
                ? selected.has(main.slug)
                : chosen === childSlugs.length
                  ? true
                  : chosen > 0
                    ? ('indeterminate' as const)
                    : false;
            const expanded = open.has(main.slug);
            const panelId = `services-${main.slug}`;
            return (
              <div
                key={main.slug}
                className={cn(
                  'rounded-xl border p-1',
                  state ? 'border-primary/40 bg-primary-soft/40' : 'border-border',
                )}
              >
                <div className="flex items-center gap-3 px-2.5 py-2">
                  <Checkbox
                    id={`cat-${main.slug}`}
                    checked={state}
                    onCheckedChange={() => toggleMain(main.slug, childSlugs, state !== true)}
                  />
                  <label htmlFor={`cat-${main.slug}`} className="flex-1 cursor-pointer text-sm font-semibold">
                    {name(main)}
                  </label>
                  {childSlugs.length > 0 && (
                    <>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {fill(s.selectedCount, { count: chosen, total: childSlugs.length })}
                      </span>
                      <button
                        type="button"
                        aria-expanded={expanded}
                        aria-controls={panelId}
                        aria-label={fill(s.showServices, { name: name(main) })}
                        onClick={() =>
                          setOpen((current) => {
                            const next = new Set(current);
                            if (expanded) next.delete(main.slug);
                            else next.add(main.slug);
                            return next;
                          })
                        }
                        className="flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface"
                      >
                        <ChevronDown
                          className={cn('size-4 transition-transform', expanded && 'rotate-180')}
                        />
                      </button>
                    </>
                  )}
                </div>
                {expanded && childSlugs.length > 0 && (
                  <div
                    id={panelId}
                    className="grid gap-0.5 border-t border-border/70 px-2.5 pb-1.5 pt-1.5 sm:grid-cols-2"
                  >
                    {main.children.map((child) => (
                      <div
                        key={child.slug}
                        className="flex items-center gap-2.5 rounded-lg px-1.5 py-1.5 hover:bg-surface"
                      >
                        <Checkbox
                          id={`cat-${child.slug}`}
                          checked={isOn(child.slug)}
                          onCheckedChange={(checked) =>
                            toggleChild(main.slug, childSlugs, child.slug, checked === true)
                          }
                        />
                        <label htmlFor={`cat-${child.slug}`} className="cursor-pointer text-sm">
                          {name(child)}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </fieldset>
      <fieldset className="grid gap-3" aria-describedby={errors.counties ? 'counties-error' : undefined}>
        <div className="flex items-center justify-between gap-3">
          <legend className="text-sm font-semibold text-secondary">{s.counties}</legend>
          <button
            type="button"
            onClick={() => onCounties(everyCounty ? [] : allCounties)}
            className="text-xs font-semibold text-primary-hover underline-offset-2 hover:underline"
          >
            {everyCounty ? s.clearAll : s.selectAll}
          </button>
        </div>
        {errors.counties && (
          <p id="counties-error" role="alert" className="text-sm text-danger">
            {errors.counties}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {(countyList.data ?? []).map((county) => {
            const on = counties.includes(county.code);
            return (
              <button
                key={county.code}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() =>
                  onCounties((list) =>
                    on ? list.filter((code) => code !== county.code) : [...list, county.code],
                  )
                }
                className={cn(
                  'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
                  'focus-visible:outline-2 focus-visible:outline-ring',
                  on
                    ? 'border-secondary bg-secondary text-secondary-foreground'
                    : 'border-border bg-surface text-muted-foreground hover:text-secondary',
                )}
              >
                {on && <Check className="size-3.5" aria-hidden />}
                {county.name}
              </button>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}
